import type { AnalyticsSeries } from '@/entities/analytics';

/** One point per day, shaped for recharts. */
export interface SeriesPoint {
  /** Short axis label, e.g. `Jul 23`. */
  label: string;
  value: number;
  /** Second value, only for the stacked sent/received card. */
  secondary?: number;
}

const AXIS_LABEL_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/** `2026-07-23` -> `Jul 23`, read as UTC to match how the backend buckets days. */
export function formatDayLabel(isoDay: string): string {
  const parsed = new Date(`${isoDay}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? isoDay : AXIS_LABEL_FORMAT.format(parsed);
}

export function toPoints(series: AnalyticsSeries | undefined): SeriesPoint[] {
  if (!series) return [];
  return series.dates.map((date, index) => ({
    label: formatDayLabel(date),
    value: series.values[index] ?? 0,
  }));
}

/**
 * Merges two series onto one axis for the stacked messages card. The backend
 * returns both over the same window, but zip defensively on the longer of the
 * two so a short series can never silently truncate the chart.
 */
export function toStackedPoints(
  primary: AnalyticsSeries | undefined,
  secondary: AnalyticsSeries | undefined,
): SeriesPoint[] {
  const dates =
    (primary?.dates.length ?? 0) >= (secondary?.dates.length ?? 0)
      ? primary?.dates
      : secondary?.dates;
  if (!dates) return [];

  return dates.map((date, index) => ({
    label: formatDayLabel(date),
    value: primary?.values[index] ?? 0,
    secondary: secondary?.values[index] ?? 0,
  }));
}

export function sum(series: AnalyticsSeries | undefined): number {
  return (series?.values ?? []).reduce((total, value) => total + value, 0);
}

export function average(series: AnalyticsSeries | undefined): number {
  const values = series?.values ?? [];
  if (values.length === 0) return 0;
  return values.reduce((total, value) => total + value, 0) / values.length;
}

/**
 * Mean across days that have data, skipping empty ones.
 *
 * For a rate like session length a zero means "nobody used it that day", not
 * "sessions lasted no time", so including those days answers a different
 * question and makes the figure depend on the window: a workspace used 3 days
 * out of 14 at 20 minutes reads 4.3 over 14 days and 8.6 over 7.
 *
 * This is NOT a true per-session average - it weights every active day equally,
 * so a day with one long session counts as much as a day with a hundred short
 * ones. Computing the weighted version needs per-day session counts, which the
 * upstream average-session endpoint does not return. Hence the card is labelled
 * as a daily average rather than a per-session one.
 *
 * A day of genuinely zero-length sessions is indistinguishable from a day with
 * none, and is treated as no data.
 */
export function averageOverActiveDays(series: AnalyticsSeries | undefined): number {
  const active = (series?.values ?? []).filter((value) => value > 0);
  if (active.length === 0) return 0;
  return active.reduce((total, value) => total + value, 0) / active.length;
}

/** Whole numbers for counts, one decimal for averages. */
export function formatCount(value: number): string {
  return Math.round(value).toLocaleString();
}

export function formatDecimal(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}
